// frontend/plugins/jquery-polyfill.client.ts
/**
 * jQuery Polyfill Plugin
 * 
 * Provides a minimal jQuery polyfill to prevent "$ is not defined" errors
 * from external scripts like GTM, Genesys, or other third-party libraries
 * that expect jQuery to be available globally.
 */

interface JQueryPolyfill {
  (selector: string | (() => void)): JQueryPolyfill;
  ready: (fn: () => void) => void;
  on: () => JQueryPolyfill;
  off: () => JQueryPolyfill;
  addClass: () => JQueryPolyfill;
  removeClass: () => JQueryPolyfill;
  hide: () => JQueryPolyfill;
  show: () => JQueryPolyfill;
  attr: () => JQueryPolyfill;
  prop: () => JQueryPolyfill;
  val: () => JQueryPolyfill;
  html: () => JQueryPolyfill;
  text: () => JQueryPolyfill;
  append: () => JQueryPolyfill;
  prepend: () => JQueryPolyfill;
  remove: () => JQueryPolyfill;
  find: () => JQueryPolyfill;
  each: () => JQueryPolyfill;
  click: () => JQueryPolyfill;
  submit: () => JQueryPolyfill;
  length: number;
}

// Add jQuery polyfill type declaration
declare global {
  interface Window {
    $?: JQueryPolyfill;
    jQuery?: JQueryPolyfill;
  }
}

export default defineNuxtPlugin(() => {
  // Only run on client-side
  if (process.client && typeof window !== "undefined" && !window.$) {
    console.log('[jQuery Polyfill] Installing global jQuery polyfill to prevent $ is not defined errors');
    
    window.$ = window.jQuery = ((selector: string | (() => void)): JQueryPolyfill => {
      console.warn('[jQuery Polyfill] jQuery not loaded, using polyfill for selector:', selector);
      
      const polyfillInstance: JQueryPolyfill = {
        ready: (fn: () => void) => { 
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', fn);
          } else {
            fn();
          }
        },
        on: () => polyfillInstance,
        off: () => polyfillInstance,
        addClass: () => polyfillInstance,
        removeClass: () => polyfillInstance,
        hide: () => polyfillInstance,
        show: () => polyfillInstance,
        attr: () => polyfillInstance,
        prop: () => polyfillInstance,
        val: () => polyfillInstance,
        html: () => polyfillInstance,
        text: () => polyfillInstance,
        append: () => polyfillInstance,
        prepend: () => polyfillInstance,
        remove: () => polyfillInstance,
        find: () => polyfillInstance,
        each: () => polyfillInstance,
        click: () => polyfillInstance,
        submit: () => polyfillInstance,
        length: 0
      } as JQueryPolyfill;
      
      return polyfillInstance;
    }) as JQueryPolyfill;
    
    console.log('[jQuery Polyfill] Global jQuery polyfill installed successfully');
  }
});
